import { useState, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { getVialPhoto } from "@/lib/productPhotos";
import { getProductRedirect } from "@/lib/productRedirects";
import { getEffectiveStock } from "@/lib/stock";
import ProductCard from "@/components/ProductCard";
import ProductContent from "@/components/ProductContent";
import ProductJsonLd from "@/components/ProductJsonLd";
import NotFoundPage from "@/pages/NotFoundPage";
import AccessoryProductView from "@/components/product/AccessoryProductView";
import CompoundProductMedia from "@/components/product/CompoundProductMedia";
import CompoundProductDetails from "@/components/product/CompoundProductDetails";
import ReconstitutionKitCta from "@/components/product/ReconstitutionKitCta";

const PEN_TABS = ["SEALED", "OPEN", "PEN", "FORMAT", "VERIFIED"];
const RAW_TABS_WITH_PHOTO = ["VIAL", "FORMAT", "VERIFIED"];
const RAW_TABS_NO_PHOTO = ["FORMAT", "VERIFIED"];

// Thin orchestrator for the product detail page. Owns the top-level state
// (tab, qty, variant, gallery index) plus SEO meta side-effects, then
// composes the correct sub-view for the SKU type:
//   - Accessory      → AccessoryProductView (self-contained variant state)
//   - Compound       → CompoundProductMedia + CompoundProductDetails
//
// Every visual + behavioural detail lives in the sub-components, this file
// is intentionally kept small so future PDP edits touch focused modules
// rather than a 1,100-line monolith.
export default function ProductPage() {
  const { slug } = useParams();
  // Withdrawn SKU? Compute now so we can short-circuit AFTER all hooks
  // below have been declared, React requires hook order be stable per
  // render, so we can't early-return before them.
  const redirect = getProductRedirect(slug);

  const [tab, setTab] = useState("SEALED");
  const [qty, setQty] = useState(1);
  const [variantIdx, setVariantIdx] = useState(0);
  const [galleryIdx, setGalleryIdx] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ["product", slug],
    queryFn: async () => (await api.get(`/products/${slug}`)).data,
    // Skip the fetch when we're about to redirect, no need to hit the
    // API for a slug we're not going to render.
    enabled: !redirect,
  });

  // Whenever the visitor switches to a different strength, clamp the qty
  // to whatever stock the new SKU has so the stepper never sits above the
  // cap of the newly selected variant.
  useEffect(() => {
    const p = data?.product;
    if (!p) return;
    const variants = Array.isArray(p.variants) ? p.variants : [];
    const active = variants[variantIdx] || null;
    const stock = getEffectiveStock(p, active ? active.pack_size : null);
    if (stock !== null && qty > stock) {
      setQty(Math.max(1, stock));
    }
  }, [data, variantIdx, qty]);

  useEffect(() => {
    // Reset the active tab to a sensible default for the loaded format.
    // Raw Vials / Vial Kits with a hero photo default to VIAL; raw vials
    // without a photo start on FORMAT so no tab looks blank.
    const p = data?.product;
    if (!p) return;
    setGalleryIdx(0);
    if (p.format === "raw" || p.format === "vial_kit") {
      const hasVial = !!getVialPhoto(p.slug);
      setTab(hasVial ? "VIAL" : "FORMAT");
    } else {
      setTab("SEALED");
    }
  }, [data]);

  useEffect(() => {
    if (!data?.product?.name) return;
    const p = data.product;
    // Templated title: "{Compound} {Strength} {Format} | Research Peptide |
    // PepDirect". Strip the "(component list)" suffix on Blend products and
    // append the standard suffix.
    const shortName = p.name.replace(/\s*\([^)]*\)\s*$/, "").trim();
    const isAccessory = !!p.is_accessory;
    const kind = isAccessory ? "Research Peptide Supplies" : "Research Peptide";
    // Per-slug SEO overrides, added narrowly for compounds that have been
    // through the individual SEO/copy pass (Part 4 onwards). Each entry
    // supplies the exact <title> and meta description agreed for that
    // product; every other slug falls back to the templated defaults.
    const SEO_OVERRIDES = {
      "bpc-157": {
        title: "BPC-157 UK | Research Peptide | PepDirect",
        description:
          "BPC-157 research peptide from PepDirect UK, with batch-specific HPLC testing, COA documentation and tracked UK dispatch. For laboratory research use only.",
      },
      "klow": {
        title: "KLOW Peptide UK | Research Peptide Blend | PepDirect",
        description:
          "KLOW research peptide blend from PepDirect UK, combining GHK-Cu, BPC-157, TB-500 and KPV with batch-specific testing and COA documentation. For laboratory research use only.",
      },
      "klow-pen": {
        title: "KLOW Peptide Pen UK | Research Blend | PepDirect",
        description:
          "KLOW peptide pen, GHK-Cu, BPC-157, TB-500 and KPV in a pre-filled format, COA-verified. For laboratory research use only, from PepDirect UK.",
      },
      "glow": {
        title: "GLOW Peptide UK | Research Peptide Blend | PepDirect",
        description:
          "GLOW peptide blend, GHK-Cu, BPC-157 and TB-500 with lot-specific COA documentation. For laboratory research use only, from PepDirect UK.",
      },
      "glow-pen": {
        title: "GLOW Peptide Pen UK | Research Blend | PepDirect",
        description:
          "GLOW peptide pen, GHK-Cu, BPC-157 and TB-500 in a pre-filled format, COA-verified. For laboratory research use only, from PepDirect UK.",
      },
      "ghk-cu": {
        title: "GHK-Cu UK | Copper Peptide | PepDirect",
        description:
          "GHK-Cu copper peptide with lot-specific testing and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "kpv": {
        title: "KPV Peptide UK | Research Peptide | PepDirect",
        description:
          "KPV research peptide with lot-specific testing and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "semax": {
        title: "Semax UK | Research Peptide | PepDirect",
        description:
          "Semax research peptide with lot-specific testing and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "nad-plus": {
        title: "NAD+ Raw Vial 500mg | Nicotinamide Adenine Dinucleotide Research | PepDirect UK",
        description:
          "NAD+ 500mg raw vial for cellular energy and redox research, COA-verified. For laboratory research use only, from PepDirect UK.",
      },
      "nad-plus-pen": {
        title: "NAD+ 500mg Pen Kit | Pre-Filled NAD+ Research Format | PepDirect UK",
        description:
          "NAD+ 500mg Pen Kit in a pre-filled research format, COA-verified. For laboratory research use only, from PepDirect UK.",
      },
      "cjc-1295-no-dac": {
        title: "CJC-1295 No DAC UK | Research Peptide | PepDirect",
        description:
          "CJC-1295 No DAC research peptide with lot-specific testing and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "cjc-1295-dac": {
        title: "CJC-1295 DAC UK | Research Peptide | PepDirect",
        description:
          "CJC-1295 DAC research peptide with lot-specific testing and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "hulk": {
        title: "HULK Peptide Blend UK | Research Blend | PepDirect",
        description:
          "HULK research blend containing CJC-1295 No DAC and Ipamorelin with lot-specific COA documentation. For laboratory research use only, from PepDirect UK.",
      },
      "hulk-pen": {
        title: "HULK Peptide Pen UK | Research Blend | PepDirect",
        description:
          "HULK peptide pen containing CJC-1295 No DAC and Ipamorelin in a pre-filled research format. For laboratory research use only, from PepDirect UK.",
      },
      "pt-141": {
        title: "PT-141 UK | Bremelanotide Research Peptide | PepDirect",
        description:
          "PT-141 (bremelanotide) 10mg research peptide with lot-specific testing and COA documentation. For laboratory research use only, from PepDirect UK.",
      },
      "ipamorelin": {
        title: "Ipamorelin UK | Research Peptide | PepDirect",
        description:
          "Ipamorelin research peptide with lot-specific testing and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "tb-500": {
        title: "TB-500 UK | Research Peptide | PepDirect",
        description:
          "TB-500 research peptide with lot-specific testing and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "selank": {
        title: "Selank UK | Research Peptide | PepDirect",
        description:
          "Selank research peptide with lot-specific testing and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "cagrilintide": {
        title: "Cagrilintide UK | Research Peptide | PepDirect",
        description:
          "Cagrilintide research peptide with lot-specific testing and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "mots-c": {
        title: "MOTS-C UK | Research Peptide | PepDirect",
        description:
          "MOTS-C research peptide with lot-specific testing and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "aod-9604": {
        title: "AOD-9604 UK | Research Peptide | PepDirect",
        description:
          "AOD-9604 research peptide with lot-specific testing and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "wolverine": {
        title: "Wolverine Peptide UK | BPC-157 + TB-500 Blend | PepDirect",
        description:
          "Wolverine peptide blend with BPC-157 and TB-500 plus lot-specific COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "wolverine-pen": {
        title: "Wolverine Peptide Pen UK | Research Blend | PepDirect",
        description:
          "Wolverine peptide pen with BPC-157 and TB-500 in a pre-filled research format. For laboratory research use only, supplied by PepDirect UK.",
      },
      "melanotan-1": {
        title: "Melanotan 1 (Melanotan I) UK | Research Peptide | PepDirect",
        description:
          "Melanotan I (Melanotan 1 / afamelanotide) research peptide with lot-specific HPLC and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "melanotan-2": {
        title: "Melanotan 2 (Melanotan II) UK | Research Peptide | PepDirect",
        description:
          "Melanotan II (Melanotan 2) research peptide with lot-specific HPLC and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
      "dsip": {
        title: "DSIP Peptide UK | Delta Sleep-Inducing Peptide | PepDirect",
        description:
          "DSIP research peptide with lot-specific HPLC and COA documentation. Delta sleep-inducing peptide supplied for laboratory research use only by PepDirect UK.",
      },
      "epithalon": {
        title: "Epitalon (Epithalon) UK | Research Peptide | PepDirect",
        description:
          "Epithalon (Epitalon) AEDG research peptide with lot-specific HPLC and COA documentation. For laboratory research use only, supplied by PepDirect UK.",
      },
    };
    const seo = SEO_OVERRIDES[p.slug];
    document.title = seo?.title || `${shortName} | ${kind} | PepDirect`;

    const desc = seo?.description || (p.short_description
      ? `${p.short_description} Third-party tested research peptide, COA published before purchase. Ships from UK & EU.`
      : `${shortName}, third-party tested research peptide, COA published before purchase. Ships from UK & EU.`);
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", desc);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://pepdirect.co/product/${p.slug}`);

    // BreadcrumbList structured data: Home → Peptides → Product.
    // Injected fresh on every PDP mount so the third crumb always
    // matches the current slug. Removed by the cleanup on unmount.
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://pepdirect.co/" },
        { "@type": "ListItem", position: 2, name: "Peptides", item: "https://pepdirect.co/peptides" },
        { "@type": "ListItem", position: 3, name: shortName, item: `https://pepdirect.co/product/${p.slug}` },
      ],
    };
    document.getElementById("breadcrumb-jsonld")?.remove();
    const breadcrumbEl = document.createElement("script");
    breadcrumbEl.type = "application/ld+json";
    breadcrumbEl.id = "breadcrumb-jsonld";
    breadcrumbEl.textContent = JSON.stringify(breadcrumb);
    document.head.appendChild(breadcrumbEl);
    return () => {
      document.getElementById("breadcrumb-jsonld")?.remove();
    };
  }, [data]);

  if (redirect) return <Navigate to={redirect} replace />;
  if (isLoading) return <div className="container-pd py-24 text-center text-subtle">Loading…</div>;
  if (!data) return <NotFoundPage />;

  const { product, related } = data;

  // Accessories render a simplified, non-compound layout.
  if (product.is_accessory) {
    return (
      <>
        <ProductJsonLd product={product} />
        <AccessoryProductView product={product} qty={qty} setQty={setQty} />
      </>
    );
  }

  const vialPhotoSrc =
    product.format === "raw" || product.format === "vial_kit"
      ? getVialPhoto(product.slug)
      : null;
  const activeTabs =
    product.format === "raw" || product.format === "vial_kit"
      ? vialPhotoSrc
        ? RAW_TABS_WITH_PHOTO
        : RAW_TABS_NO_PHOTO
      : PEN_TABS;

  return (
    <div className="container-pd py-12 lg:py-16" data-testid="product-page">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
        <CompoundProductMedia
          product={product}
          tab={tab}
          setTab={setTab}
          galleryIdx={galleryIdx}
          setGalleryIdx={setGalleryIdx}
          activeTabs={activeTabs}
        />
        <CompoundProductDetails
          product={product}
          qty={qty}
          setQty={setQty}
          variantIdx={variantIdx}
          setVariantIdx={setVariantIdx}
        />
      </div>

      {/* Structured content (Description / Specs / Research / FAQ / Legal) */}
      <ProductContent product={product} />
      <ProductJsonLd product={product} />

      {/* Reconstitution Kit cross-sell, only on Raw Vial pages, since this
          is the actual moment of need (someone with a lyo powder in hand). */}
      {product.format === "raw" && <ReconstitutionKitCta />}

      {/* Often bought together */}
      <div className="mt-20">
        <p className="label-mono text-brandblue">§ Often bought together</p>
        <h2 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
          Pair it with.
        </h2>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {related.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </div>
    </div>
  );
}
